import { createContext, useContext, useEffect, useState } from 'react';
import type { Money } from '@urbanrats/contracts';
import { formatAtomic } from '@urbanrats/domain';
import { filtersFromSearch, filtersToSearch, type OpsData, type OpsFilters, type PortalUser } from '@urbanrats/ops-core';
import type { OpsBackend } from './backend/types.ts';

// ---- routing (History API; filters live in the query string) ----------------------------------

export function useLocation(): { path: string; search: string } {
  const [loc, setLoc] = useState({ path: location.pathname, search: location.search });
  useEffect(() => {
    const h = () => setLoc({ path: location.pathname, search: location.search });
    addEventListener('popstate', h);
    addEventListener('ur:navigate', h);
    return () => {
      removeEventListener('popstate', h);
      removeEventListener('ur:navigate', h);
    };
  }, []);
  return loc;
}

export function navigate(to: string, replace = false): void {
  if (replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  dispatchEvent(new Event('ur:navigate'));
  if (!replace) window.scrollTo(0, 0);
}

/** Link target that keeps the current filters (and adds extra query keys, e.g. state/funding). */
export function hrefWith(path: string, f: OpsFilters, extra: Record<string, string | null> = {}): string {
  return `${path}${filtersToSearch(f, extra)}`;
}

export const currentFilters = (search: string) => filtersFromSearch(search);
export const param = (search: string, k: string) => new URLSearchParams(search).get(k);

// ---- formatting (fixed-point, per currency) -----------------------------------------------------

const LOCALE = 'en-US';
export function fmtAtomic(atomic: bigint, scale: number, currency: string): string {
  const fixed = formatAtomic(atomic, scale);
  const neg = fixed.startsWith('-');
  const [w = '0', f] = (neg ? fixed.slice(1) : fixed).split('.');
  return `${neg ? '−' : ''}${w.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${f !== undefined ? `.${f}` : ''} ${currency}`;
}
export const fmtMoney = (m: Pick<Money, 'amountAtomic' | 'currency' | 'scale'>) => fmtAtomic(BigInt(m.amountAtomic), m.scale, m.currency);
export const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString(LOCALE, { dateStyle: 'medium', timeStyle: 'medium', timeZone: 'UTC' }) + ' UTC' : '—');
export const fmtOdds = (o: { numerator: string; denominator: string }) => `×${formatAtomic((BigInt(o.numerator) * 100n) / BigInt(o.denominator), 2)}`;

// ---- app context --------------------------------------------------------------------------------

export interface Ctx {
  backend: OpsBackend;
  user: PortalUser;
  /** Tenant-scoped snapshot for this user. */
  data: OpsData;
  filters: OpsFilters;
  search: string;
  /** Re-read after a mutation. */
  refresh: () => void;
  nowMs: number;
}

export const AppCtx = createContext<Ctx | null>(null);
export function useCtx(): Ctx {
  const c = useContext(AppCtx);
  if (!c) throw new Error('no app context');
  return c;
}

export const STALE_AFTER_MS = 60_000;
